/**
 * @fileoverview Notification Service (admin inbox)
 *
 * Wraps the `/notifications/me/*` endpoints. Same shape as the
 * store dashboard counterpart — staff can have their own notification
 * stream (e.g. ticket assignments, escalations).
 */

import { apiClient } from './api.service';

export type NotificationCategory =
  | 'support'
  | 'order_update'
  | 'promotion'
  | 'loyalty'
  | 'billing'
  | 'system';

export interface InAppNotification {
  id: string;
  channel: string;
  priority: string;
  status: string;
  title: string;
  body: string;
  imageUrl: string | null;
  deepLink: string | null;
  category: NotificationCategory | string;
  referenceId: string | null;
  referenceType: string | null;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationListResponse {
  data: InAppNotification[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    unread: number;
  };
}

export const notificationService = {
  async listMine(
    params: { page?: number; limit?: number; unreadOnly?: boolean; category?: string } = {},
    opts: { signal?: AbortSignal } = {},
  ): Promise<NotificationListResponse> {
    const search = new URLSearchParams();
    if (params.page) search.set('page', String(params.page));
    if (params.limit) search.set('limit', String(params.limit));
    if (params.unreadOnly) search.set('unreadOnly', 'true');
    if (params.category) search.set('category', params.category);
    const qs = search.toString();
    const response = await apiClient.get<NotificationListResponse>(
      `/notifications/me${qs ? `?${qs}` : ''}`,
      { signal: opts.signal },
    );
    return response.data;
  },

  async markRead(id: string): Promise<void> {
    await apiClient.patch(`/notifications/me/${id}/read`, {});
  },

  async markAllRead(): Promise<{ count: number }> {
    const response = await apiClient.patch<{ count: number }>(
      `/notifications/me/read-all`,
      {},
    );
    return response.data;
  },
};
